import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BiPlus } from 'react-icons/bi';
import { toast } from 'react-toastify';

import { useAppDispatch, useAppSelector } from '@/app/hook';
import { useGetAllScenariosQuery } from '@/app/services/chatbot-rtk.services';
import { IGetAllScenariosResponse } from '@/app/services/chatbot-rtk.type';
import { BannerHelper } from '@/components/common/BannerToast';
import { selectUserProfile } from '@/features/auth/auth.slice';
import InfoMessageAlert from '@/features/bot/InfoMessageAlert';
import { resetCreateFlow, setType, toggeleCreateModal } from '@/features/scenario/create/create.slice';
import { WrappedErrorBoundary } from '@/pages/WrappedErrorBoundary';
import Utilities from '@/utils';
import { BotTypeEnum } from '@/utils/BotTypeEnum';
import { SumUserTypeEnum } from '@/utils/SumUserTypeEnum';

import ApiFailure from '../../../components/ui/ApiFailure';
import ApiLoading from '../../../components/ui/ApiLoading';
import ProBadge from '../../../components/ui/ProBadge';
import usePermission from '../../../hooks/usePermission';
import ChatBotItem from './ChatBotItem';

const KnowledgeBasesPage = ({ type }: { type: 'link' | 'widget' }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const profile = useAppSelector(selectUserProfile);
  const canCreateMore = Utilities.useCanCreateMoreChatbots();
  const permission = usePermission();

  const [transactionIdState, setTransactionIdState] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('transaction_id');
  });

  useEffect(() => {
    let lastSearch = window.location.search;
    const observer = new MutationObserver(() => {
      if (window.location.search !== lastSearch) {
        lastSearch = window.location.search;
        const params = new URLSearchParams(window.location.search);
        setTransactionIdState(params.get('transaction_id'));
      }
    });
    observer.observe(document, { subtree: true, childList: true });
    return () => observer.disconnect();
  }, []);

  const { isLoading, isError, refetch, isFetching, data } = useGetAllScenariosQuery(
    { botType: type, ...(transactionIdState ? { transaction_id: transactionIdState } : {}) },
    { refetchOnMountOrArgChange: true }
  );

  const [filterableDat, setFilterableData] = useState<IGetAllScenariosResponse[]>([]);

  useEffect(() => {
    if (!isFetching && data) setFilterableData(data);
  }, [isFetching, data]);

  const handleAddNewChatbot = () => {
    if (type === 'link' && !canCreateMore) {
      toast.warn(t('HelpBanner/ChatbotCreation/maxBotReachedMessage'));
      return;
    }
    if ((type === 'link' && permission?.extensionPage.upgrade) || (type === 'widget' && !permission.widgetPage.addNewBot)) return;

    dispatch(resetCreateFlow());
    dispatch(toggeleCreateModal(true));
    if (type === 'link') dispatch(setType(BotTypeEnum.HUMAN_CLONE));
  };

  const handleSearchChatbots = (value: string) => {
    if (!value || value.length === 0) return setFilterableData(data ?? []);
    const filteredData = data?.filter(item => item.title.toLowerCase().includes(value.toLowerCase()));
    setFilterableData(filteredData ?? []);
  };

  return (
    <div className="tw-px-6 tw-my-6">
      <section className="chatbots-page">
        {profile?.user_type == SumUserTypeEnum.BUSINESSPARTNER && (
          <div className="tw-w-full tw-my-3">
            <InfoMessageAlert texts={[t('KnowledgeBasesPage/t-11')]} isClose={true} />
          </div>
        )}

        <section className=" tw-mb-4">
          <BannerHelper message={type === 'link' ? t('HelpBanner/ListClone/hint') : t('HelpBanner/ListChatbot/hint')} />
        </section>

        <div className="tw-w-full tw-h-[38px] tw-px-[17px] tw-py-[9px] tw-bg-white tw-rounded-[89px] tw-border tw-border-gray-200 tw-justify-start tw-items-center tw-inline-flex tw-mb-4">
          <input
            aria-label="search"
            type="search"
            placeholder={t('KnowledgeBasesPage/l-01')}
            className="focus-visible:tw-outline-none focus:tw-outline-none tw-grow tw-shrink tw-basis-0 tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Inter'] tw-leading-tight tw-border-0"
            onKeyUp={e => {
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              handleSearchChatbots(e?.target?.value);
            }}
          />
        </div>
      </section>

      <section className="chatbots-page tw-py-1">
        <div className="tw-w-full tw-shadow tw-rounded-lg tw-flex-col tw-justify-start tw-items-start tw-inline-flex">
          <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-flex tw-gap-2">
            {isLoading ? (
              <div className="tw-self-stretch tw-px-6 tw-py-1 tw-bg-white tw-border-0 tw-border-solid tw-border-b tw-border-gray-200 tw-justify-center tw-items-center tw-gap-4 tw-inline-flex">
                <ApiLoading />
              </div>
            ) : isError ? (
              <div className="tw-self-stretch tw-px-6 tw-py-1 tw-bg-white tw-border-0 tw-border-solid tw-border-b tw-border-gray-200 tw-justify-center tw-items-center tw-gap-4 tw-inline-flex">
                <ApiFailure refetch={refetch} />
              </div>
            ) : (
              filterableDat.length > 0 &&
              filterableDat.map(item => <ChatBotItem key={item.id} source={data} data={item} onSourceMutation={setFilterableData} type={type} />)
            )}
          </div>
        </div>
      </section>

      <div className="tw-w-full tw-pt-6 tw-pb-1 tw-flex tw-flex-col tw-gap-4">
        <div className="tw-bg-white tw-rounded-lg tw-shadow">
          <div className="tw-p-2 tw-flex tw-justify-center tw-items-center">
            <button
              id="btn-add-new-chatbot"
              className="tw-flex tw-items-center tw-rounded-md tw-cursor-pointer tw-justify-center tw-whitespace-nowrap tw-bg-white tw-border-none tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight tw-h-9 tw-px-4 tw-py-2 gap-2"
              onClick={handleAddNewChatbot}
              disabled={(type === 'link' && permission?.extensionPage.upgrade) || (type === 'widget' && !permission.widgetPage.addNewBot)}
            >
              <div className="tw-p-1 tw-bg-primary tw-rounded-[19px] tw-shadow tw-justify-center tw-items-center tw-flex">
                <div className="tw-p-1 tw-bg-primary tw-rounded-[19px] tw-shadow tw-flex tw-justify-center tw-items-center">
                  <BiPlus size={22} className="tw-text-white" />
                </div>
              </div>

              <div className={` tw-text-primary tw-text-sm tw-font-semibold tw-font-['Lexend'] tw-leading-tight tw-flex tw-items-center tw-gap-2`}>
                {type === 'widget' ? t('LatestChatBotsQuickAccess/key2') : t('LatestChatBotsQuickAccess/key6')}
              </div>
            </button>

            {(type === 'link' && permission?.extensionPage.upgrade) || (type === 'widget' && !permission.widgetPage.addNewBot) ? <ProBadge /> : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WrappedErrorBoundary(KnowledgeBasesPage);
